import React from "react";
import { motion } from "framer-motion";
import { Home, MapPin } from "lucide-react";
import { Kiosk } from "../types";

type Screen = "home" | "browse" | "find" | "directions" | "map3d";

export interface NavigationBarProps {
  currentScreen: Screen;
  kioskData: Kiosk | null;
  onNavigate: (screen: Screen) => void;
}

interface NavButtonProps {
  label: string;
  icon: React.ComponentType<any>;
  active: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({
  label,
  icon: Icon,
  active,
  onClick,
}) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all min-w-[44px] min-h-[44px] ${
      active ? "text-white shadow-md" : "hover:shadow-md"
    }`}
    style={{
      backgroundColor: active ? "#3B82F6" : "var(--surface-3)",
      color: active ? "#ffffff" : "var(--color-text-secondary)",
    }}
    aria-label={label}
    aria-current={active ? "page" : undefined}
  >
    <Icon size={20} />
    <span className="hidden sm:inline">{label}</span>
  </motion.button>
);

// Derive a readable floor label from kioskData
function getFloorLabel(kioskData: any): string {
  const floor = kioskData?.floor || (kioskData as any)?.floor_id || "";
  if (!floor) return "";
  if (floor === "G" || floor === "G1") return "Floor G";
  if (floor === "B1") return "Floor B1";
  return `Floor ${floor}`;
}

const NavigationBar: React.FC<NavigationBarProps> = ({
  currentScreen,
  kioskData,
  onNavigate,
}) => {
  const floorLabel = kioskData ? getFloorLabel(kioskData) : "";
  const kioskName = kioskData ? kioskData.name || kioskData.id : "";
  const locationText = floorLabel ? `${floorLabel} · ${kioskName}` : kioskName;

  return (
    <nav
      className="sticky top-0 z-50 shadow-md border-b"
      style={{
        backgroundColor: "var(--surface-2)",
        borderColor: "var(--surface-3)",
      }}
    >
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        {/* Home button */}
        <div className="flex items-center gap-3">
          <NavButton
            label="Home"
            icon={Home}
            active={currentScreen === "home"}
            onClick={() => onNavigate("home")}
          />
        </div>

        {/* Location indicator — floor + kiosk name */}
        {kioskData && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: "var(--surface-3)",
              color: "var(--color-text-secondary)",
            }}
          >
            <MapPin size={18} className="text-red-400" />
            <span className="text-sm font-medium">
              You are at:{" "}
              <span
                className="font-bold"
                style={{ color: "var(--color-text-primary)" }}
              >
                {locationText}
              </span>
            </span>
          </motion.div>
        )}

        {/* Right side — empty (Directions button removed) */}
        <div style={{ width: 44 }} />
      </div>

      {/* Mobile location indicator */}
      {kioskData && (
        <div
          className="md:hidden flex items-center justify-center gap-2 px-4 py-2 border-t"
          style={{
            backgroundColor: "var(--surface-3)",
            borderColor: "var(--surface-4)",
            color: "var(--color-text-secondary)",
          }}
        >
          <MapPin size={16} className="text-red-400" />
          <span className="text-xs font-medium">
            You are at:{" "}
            <span
              className="font-bold"
              style={{ color: "var(--color-text-primary)" }}
            >
              {locationText}
            </span>
          </span>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;

